import React, { useEffect, useRef, useState } from 'react';
import '../../styles/Sports.css';
import { useSport } from '../../context/SportContext.tsx';
import { MoonLoader } from "react-spinners";
import { FaCaretDown } from "react-icons/fa";
import { LeagueModel, SportModel } from '../../models/Sport';
import { FaAngleDown } from "react-icons/fa";
import { FaAngleRight } from "react-icons/fa6";
import { TbAntenna } from "react-icons/tb";
import { FaTv } from "react-icons/fa";
import { GiCardAceSpades } from "react-icons/gi";
import { Link, useLocation } from 'react-router-dom';
import { logUserActivity } from '../../services/player/LoginService.tsx';
import { useUser } from '../../context/UserContext.tsx';
import AlertError from '.././alerts/AlertError.tsx';
import ImageWithPlaceholder from '../ImageWithPlaceholder.tsx';
import { InfoModal } from './InfoModal.tsx';
import { getScheduleDateRange, PREGAME_SCHEDULE_EXTRA_DAYS } from '../../helpers/scheduleDateRange.ts';
import { scrollPageToTop } from '../../helpers/scrollPageToTop.ts';
import { canAccessVimplayGames } from '../../services/player/VimplayService.tsx';
// import { streamOdds } from '../services/apis/ApiLive.tsx';
// const hasCheckedLeagues = Object.values(checkedLeagues).some(value => value === true);

interface SportsScheduleProps {
  setShowFooter: React.Dispatch<React.SetStateAction<boolean>>;
}

function SportsMenuSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="sports-menu-skeleton" aria-hidden="true" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i}>
          <div className="trigger sports-menu-skeleton-row">
            <span className="sports-menu-skeleton-icon" />
            <span className="sports-menu-skeleton-line" />
            <span className="sports-menu-skeleton-caret" />
          </div>
          <span className="line" />
        </div>
      ))}
    </div>
  );
}

const BASKETBALL_LEAGUE_ORDER = ["NBA", "College Basketball", "WNBA"];

function sortLeaguesForSportMenu(sportName: string, leagues: LeagueModel[]) {
  return [...leagues].sort((a, b) => {
    const aIsNfl = String(a.name).toUpperCase() === "NFL";
    const bIsNfl = String(b.name).toUpperCase() === "NFL";
    if (aIsNfl !== bIsNfl) return aIsNfl ? -1 : 1;

    if (sportName === "Basketball") {
      const indexA = BASKETBALL_LEAGUE_ORDER.indexOf(a.name);
      const indexB = BASKETBALL_LEAGUE_ORDER.indexOf(b.name);
      return (
        (indexA === -1 ? BASKETBALL_LEAGUE_ORDER.length : indexA) -
        (indexB === -1 ? BASKETBALL_LEAGUE_ORDER.length : indexB)
      );
    }

    return 0;
  });
}

export const useIsSmallScreen = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    window.matchMedia("(max-width: 1000px)").matches
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1000px)");
    const handleChange = () => setIsSmallScreen(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    window.addEventListener("resize", handleChange);
    return () => {
      media.removeEventListener("change", handleChange);
      window.removeEventListener("resize", handleChange);
    };
  }, []);

  return isSmallScreen;
};

/*Component to display the available sports options obtained from the database */
function SportsSchedule({ setShowFooter }: SportsScheduleProps) {
  const { user, userIpData } = useUser();
  if (!user?.userName) {
    throw new Error("User not authenticated");
  }
  const { showSports, setShowSports, showOptions, sports, error, loading, loadSports, loadGames,
    checkedLeagues, setSelectedGames, loadGamesFuture, setShowOptions, isParlay, setShowHomeButton, errorMessages, 
    showLimitAlert, handleCloseLimitAlert } = useSport();
  const isSmallScreen = useIsSmallScreen();
  const location = useLocation();
  const hidePropAndLive = isParlay || location.pathname === "/home/parlay";
  const [openFutures, setOpenFutures] = useState<{ [key: string]: boolean }>({});
  const hasCheckedLeagues = checkedLeagues && Object.keys(checkedLeagues).length > 0 && Object.values(checkedLeagues).some(value => value === true);
  const parentBoxesRef = useRef<Record<number, HTMLElement>>({});
  const futureBoxesRef = useRef<Record<string, HTMLElement>>({});
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadSports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sports) return;
    if (isSmallScreen) return;

    for (const sport of sports) {
      const league = sport.leagues?.find((league: any) => league.name === "MLB");
      if (league) {
        const leagueKey = `${sport.name}-${league.name}`;
        if (!checkedLeagues[leagueKey]) {
          // Main markets only — avoid duplicate bootstrap call with isMain undefined
          const { startDateAfter, startDateBefore } = getScheduleDateRange({ extraDays: PREGAME_SCHEDULE_EXTRA_DAYS, league });
          loadGames(sport.name, league, undefined, undefined, undefined, true, startDateAfter, startDateBefore);
          setSelectedGames(true);
        }

        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sports, isSmallScreen]);

  const filteredSports = sports.map((sport: any) => {
    const activeLeagues = sport.leagues.filter((league: any) => league.isActive).map((league: any) => {
      const activeMarkets = league.markets ? league.markets.filter((market: any) => market.is_active) : [];
      return {
        ...league,
        markets: activeMarkets
      };
    });
    return {
      ...sport,
      leagues: sortLeaguesForSportMenu(sport.name, activeLeagues)
    };
  }).filter((sport: any) => {
    const hasActiveLeagues = sport.leagues.length > 0;

    return hasActiveLeagues;
  });

  const handleCheckboxChange = (sport: SportModel, league: LeagueModel, marketKey?: string, markets?: string[], isMain?: boolean, startDateAfter?: string, startDateBefore?: string) => {
    const marketArray = markets ?? [];

    loadGames(sport.name, league, marketKey, marketArray, undefined, isMain, startDateAfter, startDateBefore);
    if (!isSmallScreen) {
      setSelectedGames(true)
    }
  };

  const handleCheckboxFuture = (sport: SportModel, league: any, futureName: string | string[]) => {
    const futures = Array.isArray(futureName) ? futureName : futureName.includes(',') ? futureName.split(',').map(f => f.trim()) : [futureName];
    loadGamesFuture(sport.name, league, futures);

    if (!isSmallScreen) {
      setSelectedGames(true);
    }
  };

  const handleContinueClick = () => {
    if (checkedLeagues)
      setShowSports(false);
    setSelectedGames(true);
    setShowFooter(false);
    setShowHomeButton(true);

    if (!isSmallScreen) return;

    scrollPageToTop();
    requestAnimationFrame(() => scrollPageToTop());
    window.setTimeout(scrollPageToTop, 50);
    window.setTimeout(scrollPageToTop, 150);
  };

  const toggleFuture = (key: any) => {
    const sportName = key.split('-')[0];
    const parentKey = filteredSports.findIndex((sport: any) => sport.name === sportName);
    if (parentKey === -1) return;

    setOpenFutures((prev) => {
      const newState = { ...prev };
      newState[key] = !prev[key];
      return newState;
    });
  };

  useEffect(() => {
    Object.entries(openFutures).forEach(([futureKey, isOpen]) => {
      const futureBox = futureBoxesRef.current[futureKey];
      if (!futureBox) return;

      if (isOpen) {
        const fullHeight = futureBox.scrollHeight;
        futureBox.style.maxHeight = fullHeight + "px";

      } else {
        futureBox.style.maxHeight = "0px";
      }

    });
  }, [openFutures]);

  useEffect(() => {
    const observers: ResizeObserver[] = [];

    Object.entries(futureBoxesRef.current).forEach(([key, futureBox]) => {
      if (!futureBox) return;

      const sportName = key.split('-')[0];
      const parentKey = filteredSports.findIndex((sport: any) => sport.name === sportName);
      if (parentKey === -1) return;
      const parentBox = parentBoxesRef.current[parentKey];
      if (!parentBox) return;

      const resizeObserver = new ResizeObserver(() => {
        if (!showOptions[parentKey]) return;
        requestAnimationFrame(() => {
          const nextHeight = parentBox.scrollHeight + 'px';
          if (parentBox.style.maxHeight !== nextHeight) {
            parentBox.style.maxHeight = nextHeight;
          }
        });
      });

      resizeObserver.observe(futureBox);
      observers.push(resizeObserver);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [openFutures, filteredSports, showOptions]);

  const handleToggleBox = (key: number) => {
    setShowOptions((prev: any) => {
      const newShow = [...prev];
      const willClose = newShow[key];
      newShow[key] = !willClose;

      if (willClose) {
        Object.entries(openFutures).forEach(([futureKey, isFutureOpen]) => {
          if (futureKey.startsWith(filteredSports[key].name) && isFutureOpen) {
            const futureBox = futureBoxesRef.current[futureKey];
            if (futureBox) {
              futureBox.style.maxHeight = '0px';
            }
          }
        });
      } else {
        Object.entries(openFutures).forEach(([futureKey, isFutureOpen]) => {
          if (futureKey.startsWith(filteredSports[key].name) && isFutureOpen) {
            const futureBox = futureBoxesRef.current[futureKey];
            if (futureBox) {
              requestAnimationFrame(() => {
                futureBox.style.maxHeight = futureBox.scrollHeight + 'px';
              });
            }
          }
        });
      }
      return newShow;
    });
  };

  useEffect(() => {
    showOptions.forEach((isOpen: any, key: any) => {
      const box = parentBoxesRef.current[key];
      if (!box) return;

      if (isOpen) {
        box.style.transition = 'max-height 0.35s ease';
        box.style.maxHeight = '0px';
        void box.offsetHeight;

        requestAnimationFrame(() => {
          box.style.maxHeight = box.scrollHeight + 'px';
        });
      } else {
        box.style.maxHeight = box.scrollHeight + 'px';
        requestAnimationFrame(() => {
          box.style.maxHeight = '0px';
        });
      }
    });
  }, [showOptions]);

  useEffect(() => {
    if (!isSmallScreen) {
      setShowSports(true);
      setShowFooter(true);
      return;
    }
    if (showSports) {
      setShowFooter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen, showSports]);

  const wasShowingSports = useRef(showSports);
  useEffect(() => {
    if (showSports && !wasShowingSports.current) {
      scrollPageToTop();
      requestAnimationFrame(() => scrollPageToTop());
      const t = window.setTimeout(scrollPageToTop, 50);
      wasShowingSports.current = showSports;
      return () => window.clearTimeout(t);
    }
    wasShowingSports.current = showSports;
  }, [showSports]);


  useEffect(() => {
    if (!sports || sports.length === 0) return;

    const visibleSports = sports.map((sport: any) => {
      const activeLeagues = (sport.leagues ?? []).filter((league: any) => league.isActive).map((league: any) => {
        const activeMarkets = league.markets ? league.markets.filter((market: any) => market.is_active) : [];
        return {
          ...league,
          markets: activeMarkets
        };
      });
      return {
        ...sport,
        leagues: activeLeagues
      };
    }).filter((sport: any) => sport.leagues.length > 0);

    if (visibleSports.length === 0) return;

    const openSportIndex = visibleSports.findIndex((sport: SportModel) => sport.isOpen);

    if (isSmallScreen) {
      setShowOptions(() => {
        const newArr = Array(visibleSports.length).fill(false);
        if (openSportIndex !== -1) newArr[openSportIndex] = true;
        return newArr;
      });
      return;
    }

    setShowOptions((prev: any) => {
      const newArr = Array(visibleSports.length).fill(false);
      prev.forEach((v: any, i: any) => {
        if (i < newArr.length) newArr[i] = v;
      });
      return newArr;
    });

    visibleSports.forEach((sport: any, sportIndex: number) => {
      const hasCheckedLeague = sport.leagues?.some(
        (league: any) => checkedLeagues[`${sport.name}-${league.name}`]
      );
      const shouldOpen = hasCheckedLeague || openSportIndex === sportIndex;

      if (shouldOpen) {
        setShowOptions((prev: any) => {
          const next = [...prev];
          next[sportIndex] = true;
          return next;
        });

        const element = parentBoxesRef.current[sportIndex];
        if (element) {
          element.style.maxHeight = element.scrollHeight + "px";
        }
      } else {
        setShowOptions((prev: any) => {
          const next = [...prev];
          next[sportIndex] = false;
          return next;
        });

        const element = parentBoxesRef.current[sportIndex];
        if (element) {
          element.style.maxHeight = "0px";
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sports, isSmallScreen]);

  if (error) {
    return <div>Error: {error}</div>;
  }
  if (!sports) {
    return <div>There are no options</div>
  }

  const sportsMenuMobileClass =
    isSmallScreen && showSports
      ? `sports-menu-mobile${hasCheckedLeagues ? " sports-menu-mobile--has-footer" : ""}`
      : "";

  return (
    <div className={sportsMenuMobileClass}>
      {showSports && (
        <div className="sport">
          {loading && sports.length > 0 && isSmallScreen && (
            <div className="loading-overlay-menu">
              <div className="loading-message">
                <MoonLoader color="#b6cc18" loading={loading} size={55} />
              </div>
            </div>
          )}
          <div>
            <h6 className="p teko text-center">SPORTS SCHEDULE</h6>
          </div>
          <div className="mt-2">
            {!hidePropAndLive && (
              <>
                <Link to="/propsLeagues" className={`trigger no-link`} onClick={(e) => {
                  if (user?.status !== "Active") {
                    e.preventDefault();
                    setErrorMessage("Your account is in read-only mode.");
                    return;
                  }
                  logUserActivity(user.userName, "Prop Betting", userIpData);
                }}>
                  <TbAntenna className="logo-sport-pb" />
                  <span className="sports">Prop Betting, Build SGP</span>
                </Link>
                <span className="line"></span>

                <div>
                  <div>
                    <Link to="/commercialLive" className={`trigger no-link`} onClick={(e) => {
                      if (user?.status !== "Active") {
                        e.preventDefault();
                        setErrorMessage("Your account is in read-only mode.");
                        return;
                      }
                      logUserActivity(user.userName, "PropLive", userIpData);
                    }}><FaTv className="logo-sport-lb" />
                      <span className="sports">Live Betting</span>
                    </Link>
                  </div>
                </div>
              </>
            )}


            {canAccessVimplayGames(user) ? (
              <>
                {!hidePropAndLive && <span className="line"></span>}
                <Link to="/vimplay" className="trigger no-link" onClick={() => logUserActivity(user.userName, "View Games", userIpData)}>
                  <GiCardAceSpades className="logo-sport-dice" />
                  <span className="sports">Casino Games</span>
                </Link>
                <span className="line"></span>
              </>
            ) : (
              !hidePropAndLive && <span className="line"></span>
            )}
            {loading && sports.length === 0 ? (
              <SportsMenuSkeleton />
            ) : filteredSports.map((sport: any, key: any) => (
              <div key={key}> <div className={`trigger body-customer ${showOptions[key] ? 'active' : ''}`} onClick={() => { handleToggleBox(key); }}>
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }} >
                  <ImageWithPlaceholder src={sport.logo} className={`sport-logo ${sport.id.toLowerCase()}`} alt="" placeholderClassName="placeholder-sport" />
                </div>
                <span className="sports">{sport.name}</span>
                <FaCaretDown className={`sport-icon ${showOptions[key] ? 'rotated' : ''}`} />
                <FaAngleDown className={`sport-icon-Responsive ${showOptions[key] ? 'rotated' : ''}`} />
              </div>
                <span className="line"></span>
                <div ref={el => { if (el) parentBoxesRef.current[key] = el; }} className={`box sport-img sport-${sport.name.toLowerCase()}`} style={{ overflow: 'hidden', maxHeight: '0px', transition: 'max-height 0.35s ease' }}>
                  {sport.leagues?.map((league: any, index: any) => {
                    const leagueKey = `${sport.name}-${league.name}`;
                    // const isPGA = league.name === "PGA";

                    // if (isPGA) return <div key={index} style={{ display: "none" }} />;

                    return (
                      <div key={index}>
                        {sport.name?.toLowerCase() !== "soccer" && (
                          <div style={{ marginLeft: '20px' }}>
                            <label className="leagues sports">{league.name}</label>
                          </div>
                        )}
                        <div className={`opcion-leagues d-flex justify-content-center align-items-center ${checkedLeagues[leagueKey] ? "active-league-sport" : ""}`}
                          onClick={() => {
                            const { startDateAfter, startDateBefore } = getScheduleDateRange({ extraDays: PREGAME_SCHEDULE_EXTRA_DAYS, league });
                            handleCheckboxChange(sport, league, undefined, undefined, true, startDateAfter, startDateBefore);
                          }} >
                          <input type="checkbox" className="checkbox" id={`option-${sport}-${league.name}-${index + 1}`} checked={checkedLeagues[leagueKey] ?? false} readOnly />
                          <FaAngleRight className="league-icon" />
                          <label htmlFor={`option-${sport}-${league.name}-${index + 1}`} className="sport-league">
                            {league.name}
                          </label>
                        </div>


                        {league.markets?.filter((market: any) => {
                          if (!market.selections?.length) return false;
                          return true;
                        }).map((market: any, mi: number) => {
                          const visualKey = `${sport.name}-${league.name}-${market.name}`;
                          const isChecked = market.individualMarkets ? market.selections.every((sel: string) => checkedLeagues[`${sport.name}-${league.name}-${sel}`]) : checkedLeagues[visualKey] ?? false;
                          return (
                            <div key={mi} className="nba-subdivisions mb-1">
                              <div className={`opcion-leagues d-flex justify-content-center align-items-center ${isChecked ? "active-league-sport" : ""}`}
                                onClick={() => {
                                  const { startDateAfter, startDateBefore } = getScheduleDateRange({ extraDays: PREGAME_SCHEDULE_EXTRA_DAYS, league, market });
                                  if (market.individualMarkets) {
                                    market.selections.forEach((sel: any) => {
                                      loadGames(sport.name, league, sel, [sel], "Special", true, startDateAfter, startDateBefore);
                                    });
                                  } else {
                                    loadGames(sport.name, league, market.name, market.selections, undefined, true, startDateAfter, startDateBefore);
                                  }
                                }}>
                                <input type="checkbox" className="checkbox" checked={isChecked} id={`option-${sport.name}-${league.name}-${market.name}-${mi}`} readOnly />
                                <FaAngleRight className="league-icon" />
                                <label htmlFor={`option-${sport.name}-${league.name}-${market.name}-${mi}`} className="sport-league">
                                  {market.name}
                                </label>

                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}


                  {!isParlay && sport.leagues?.map((league: any, leagueIndex: any) => (
                    <div key={`future-league-${leagueIndex}`} className="futures-container" >

                      {league.futureCategories?.map((cat: any, catIndex: any) => {
                        const leagueKey = `${sport.name}-${league.name}-${cat.category}`;
                        const itemsArray = Array.isArray(cat.items) ? cat.items : cat.items && typeof cat.items === "object" ? [cat.items] : [];

                        return (
                          <div key={`future-category-${catIndex}`} className="future-category-container leagues-future">
                            <div className="futures-header d-flex align-items-center" onClick={() => toggleFuture(leagueKey)} style={{ marginLeft: '20px' }}>

                              <FaAngleDown className="icon-future" />
                              <span className="sport-league spacig-future">
                                {league.name} — {cat.category}
                              </span>

                            </div>

                            <div className="futures-content" ref={(el) => { if (el) futureBoxesRef.current[leagueKey] = el; }}>
                              {itemsArray.map((futureObj: any, idx: any) => {
                                const leagueItemKey = `${sport.name}-${league.name}-${Array.isArray(futureObj.selections) ? futureObj.selections.join('_').replace(/[, ]+/g, '_') : futureObj.selections.replace(/[, ]+/g, '_')}`;

                                return (
                                  <div key={`future-item-${idx}`} className="nhl-subdivisions" >
                                    <div className={`opcion-leagues d-flex align-items-center ${checkedLeagues[leagueItemKey] ? "active-league-sport" : ""}`}
                                      onClick={() => handleCheckboxFuture(sport, league.name, futureObj.selections)}>

                                      <input type="checkbox" className="checkbox" id={`option-${leagueItemKey}-${idx}`} checked={checkedLeagues[leagueItemKey] ?? false} readOnly />
                                      <FaAngleRight className="league-icon" />
                                      <label htmlFor={`option-${leagueItemKey}-${idx}`} className="sport-league">
                                        {futureObj.name}
                                      </label>

                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                    </div>
                  ))}

                  {showOptions[key] && (sport.leagues === undefined || sport.leagues.length === 0) && (
                    <div></div>
                  )}
                </div>
              </div>
            ))}

          </div>
        </div>
      )
      }

      {
        errorMessage && (
          <AlertError
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )
      }

      {showLimitAlert && (
        <InfoModal
          message={errorMessages}
          onClose={handleCloseLimitAlert}
        />
      )}

      {
        isSmallScreen && showSports && hasCheckedLeagues && (
          <div className='containerButtonLeagues'>
            <span className='gameTime'>Let’s Go!</span>
            <button className='button-fixed' onClick={handleContinueClick}>
              CONTINUE
            </button>
          </div>
        )
      }


    </div >
  );
}

export default SportsSchedule;


